import { useState } from 'react';
import { isAxiosError } from 'axios';
import type {
  LinkDocumentFeedConnectionState,
  LinkDocumentFeedProblem,
  LinkExportRetryOutcome,
  LinkStatusView,
} from '@reform-lite/contracts';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import { Table, TBody, TD, TH, THead, TR } from '@/components/ui/table';
import { useT } from '@/i18n/use-t';
import { IP4_QUEUE_LABEL_KEY, type Ip4Source } from '@/hooks/use-ip4-sources';
import { useLinkStatus, useRetryLinkExport } from '@/hooks/use-link-status';

/**
 * VL-353 — what a store's Link last reported about its document feed: whether
 * it is connected to the broker and since when, what each queue still has to
 * send, and the documents that need an operator.
 *
 * Back Office cannot reach a Link, so everything here is the Link's last
 * report, and a retry is a request the Link collects on its next one. A
 * report that is not fresh is not shown at all: an old count must never read
 * as the current one. A Link that reports no feed — one built before the feed
 * existed, or one that has not received the source yet — is shown as "not
 * reported", which is not an error.
 */
export function DocumentFeedStatusPanel({
  sources,
  canEdit,
}: {
  /** The stores that have a document feed. */
  sources: readonly Ip4Source[];
  canEdit: boolean;
}): JSX.Element | null {
  const t = useT('admin_ui');
  const [picked, setPicked] = useState<string | null>(null);
  const source = sources.find((row) => row.id === picked) ?? sources[0] ?? null;
  if (source === null) return null;

  return (
    <section
      aria-label={t('settings.integrations.ip4.status.title')}
      className="space-y-3 border-t border-border pt-5"
    >
      <div>
        <h3 className="text-sm font-semibold">{t('settings.integrations.ip4.status.title')}</h3>
        <p className="max-w-3xl text-xs text-muted-foreground">
          {t('settings.integrations.ip4.status.description')}
        </p>
      </div>
      {sources.length > 1 && (
        <div className="flex max-w-md items-center gap-2">
          <Label htmlFor="document-feed-status-store">
            {t('settings.integrations.ownership.store')}
          </Label>
          <Select
            id="document-feed-status-store"
            value={source.id}
            onChange={(event) => setPicked(event.target.value)}
          >
            {sources.map((row) => (
              <option key={row.id} value={row.id}>
                {row.location.name} ({row.location.code})
              </option>
            ))}
          </Select>
        </div>
      )}
      <FeedStatus key={source.id} locationId={source.location.id} canEdit={canEdit} />
    </section>
  );
}

const CONNECTION_LABEL_KEY: Record<LinkDocumentFeedConnectionState, string> = {
  disabled: 'settings.integrations.ip4.status.connection.disabled',
  connecting: 'settings.integrations.ip4.status.connection.connecting',
  connected: 'settings.integrations.ip4.status.connection.connected',
  down: 'settings.integrations.ip4.status.connection.down',
};

const CONNECTION_VARIANT: Record<
  LinkDocumentFeedConnectionState,
  'success' | 'destructive' | 'outline'
> = {
  disabled: 'outline',
  connecting: 'outline',
  connected: 'success',
  down: 'destructive',
};

const PROBLEM_STATUS_LABEL_KEY: Record<LinkDocumentFeedProblem['status'], string> = {
  failed: 'settings.integrations.exportStatus.status.failed',
  blocked: 'settings.integrations.ip4.status.blocked',
};

const RETRY_OUTCOME_LABEL_KEY: Record<LinkExportRetryOutcome, string> = {
  queued: 'settings.integrations.exportStatus.retry.queued',
  not_found: 'settings.integrations.exportStatus.retry.not_found',
  not_retryable: 'settings.integrations.exportStatus.retry.not_retryable',
  unsupported: 'settings.integrations.ip4.status.retryUnsupported',
};

function errorText(error: unknown, fallback: string): string {
  if (isAxiosError(error)) {
    const body = error.response?.data as { message?: unknown } | undefined;
    if (typeof body?.message === 'string') return body.message;
  }
  return fallback;
}

function when(value: string | null): string {
  if (value === null) return '—';
  return new Intl.DateTimeFormat(undefined, { dateStyle: 'short', timeStyle: 'short' }).format(
    new Date(value),
  );
}

function FeedStatus({
  locationId,
  canEdit,
}: {
  locationId: string;
  canEdit: boolean;
}): JSX.Element {
  const t = useT('admin_ui');
  const query = useLinkStatus(locationId);
  const view: LinkStatusView | undefined = query.data;

  if (query.isError) {
    return (
      <p role="alert" className="text-xs text-destructive">
        {t('settings.integrations.ip4.status.loadError')}
      </p>
    );
  }
  if (view === undefined) {
    return (
      <p className="text-xs text-muted-foreground">
        {t('settings.integrations.ip4.status.loading')}
      </p>
    );
  }
  if (view.device === null) {
    return (
      <p className="text-xs text-muted-foreground">
        {t('settings.integrations.exportStatus.noLink')}
      </p>
    );
  }
  // Never reported, or not recently: nothing the Link said earlier is current.
  if (view.state === 'missing' || view.state === 'stale') {
    return (
      <p role="status" className="text-xs text-destructive">
        {t('settings.integrations.ip4.status.stale')}
      </p>
    );
  }
  const feed = view.documentFeed ?? null;
  if (feed === null) {
    return (
      <p role="status" className="text-xs text-muted-foreground">
        {t('settings.integrations.ip4.status.notReported')}
      </p>
    );
  }
  return <FeedReport locationId={locationId} view={view} feed={feed} canEdit={canEdit} />;
}

type Feed = NonNullable<LinkStatusView['documentFeed']>;

function FeedReport({
  locationId,
  view,
  feed,
  canEdit,
}: {
  locationId: string;
  view: LinkStatusView;
  feed: Feed;
  canEdit: boolean;
}): JSX.Element {
  const t = useT('admin_ui');
  const retry = useRetryLinkExport();
  const [error, setError] = useState<string | null>(null);

  async function retryRow(exportId: string): Promise<void> {
    setError(null);
    try {
      await retry.mutateAsync({ locationId, exportId });
    } catch (cause) {
      setError(errorText(cause, t('settings.integrations.exportStatus.retryError')));
    }
  }

  function requestState(problemId: string): string | null {
    // The newest request for this document: an answered one may be followed
    // by another after the document failed again.
    const request = view.retryRequests
      .filter((row) => row.exportId === problemId)
      .sort((a, b) => b.requestedAt.localeCompare(a.requestedAt))[0];
    if (request === undefined) return null;
    if (request.completedAt === null) {
      return request.deliveredAt === null
        ? t('settings.integrations.exportStatus.retry.waiting')
        : t('settings.integrations.exportStatus.retry.delivered');
    }
    return request.outcome === null ? null : t(RETRY_OUTCOME_LABEL_KEY[request.outcome]);
  }

  const listed = feed.problems.length;
  const counted = feed.queues.reduce((sum, queue) => sum + queue.failed + queue.blocked, 0);

  return (
    <div className="space-y-3 rounded-md border border-border p-3 text-sm">
      <dl className="grid grid-cols-[max-content_1fr] gap-x-4 gap-y-1 text-xs">
        <dt className="text-muted-foreground">
          {t('settings.integrations.ip4.status.connection')}
        </dt>
        <dd className="flex flex-wrap items-center gap-2">
          <Badge variant={CONNECTION_VARIANT[feed.connection.state]}>
            {t(CONNECTION_LABEL_KEY[feed.connection.state])}
          </Badge>
          {feed.connection.since !== null && (
            <span>
              {t('settings.integrations.ip4.status.since')} {when(feed.connection.since)}
            </span>
          )}
        </dd>
        <dt className="text-muted-foreground">{t('settings.integrations.ip4.status.lastError')}</dt>
        <dd
          className={
            feed.connection.lastError === null
              ? undefined
              : feed.connection.state === 'connected'
                ? 'break-words text-amber-700 dark:text-amber-400'
                : 'break-words text-destructive'
          }
        >
          {feed.connection.lastError ?? '—'}
        </dd>
        <dt className="text-muted-foreground">
          {t('settings.integrations.exportStatus.reportedAt')}
        </dt>
        <dd>{when(feed.reportedAt)}</dd>
      </dl>

      {!feed.enabled && (
        <p role="note" className="text-xs text-muted-foreground">
          {t('settings.integrations.ip4.status.paused')}
        </p>
      )}

      <Table aria-label={t('settings.integrations.ip4.status.queues')}>
        <THead>
          <TR>
            <TH scope="col">{t('settings.integrations.ip4.form.queue')}</TH>
            <TH scope="col">{t('settings.integrations.status')}</TH>
            <TH scope="col">{t('settings.integrations.exportStatus.status.pending')}</TH>
            <TH scope="col">{t('settings.integrations.exportStatus.status.failed')}</TH>
            <TH scope="col">{t('settings.integrations.ip4.status.blocked')}</TH>
            <TH scope="col">{t('settings.integrations.ip4.status.lastPublished')}</TH>
            <TH scope="col">{t('settings.integrations.ip4.status.oldestPending')}</TH>
          </TR>
        </THead>
        <TBody>
          {feed.queues.map((queue) => (
            <TR key={queue.key}>
              <TH
                scope="row"
                className="text-sm font-medium normal-case tracking-normal text-foreground"
              >
                {t(IP4_QUEUE_LABEL_KEY[queue.key])}
              </TH>
              <TD>
                <Badge
                  variant={
                    queue.enabled && feed.enabled
                      ? queue.stopped !== null && queue.stopped !== undefined
                        ? 'destructive'
                        : 'success'
                      : 'outline'
                  }
                >
                  {queue.enabled && feed.enabled
                    ? queue.stopped !== null && queue.stopped !== undefined
                      ? queue.stopped.reason === 'legacy_lottery_rows'
                        ? t(
                            'settings.integrations.ip4.status.stopped.legacyLotteryRows',
                            'Stopped: legacy lottery identities require attention',
                          )
                        : t(
                            'settings.integrations.ip4.status.stopped.legacyCheckFailed',
                            'Stopped: lottery identity could not be checked',
                          )
                      : t('settings.integrations.ip4.status.queueOn')
                    : t('settings.integrations.ip4.paused')}
                </Badge>
                {queue.enabled &&
                  feed.enabled &&
                  queue.stopped?.rows !== null &&
                  queue.stopped?.rows !== undefined && (
                    <span className="ml-1 text-xs">
                      {queue.stopped.rows === 1000 ? '1000+' : queue.stopped.rows}
                    </span>
                  )}
              </TD>
              <TD>{queue.pending}</TD>
              <TD>
                <Badge variant={queue.failed > 0 ? 'destructive' : 'outline'}>{queue.failed}</Badge>
              </TD>
              <TD>
                <Badge variant={queue.blocked > 0 ? 'destructive' : 'outline'}>
                  {queue.blocked}
                </Badge>
              </TD>
              <TD>
                <div>{when(queue.lastPublishedAt)}</div>
                {queue.lastPublishedNumber !== null && (
                  <div className="font-mono text-xs text-muted-foreground">
                    {t('settings.integrations.ip4.status.number')} {queue.lastPublishedNumber}
                  </div>
                )}
              </TD>
              <TD>{when(queue.oldestPendingAt)}</TD>
            </TR>
          ))}
        </TBody>
      </Table>
      <p className="text-xs text-muted-foreground">
        {t('settings.integrations.ip4.status.countsHint')}
      </p>

      <section aria-label={t('settings.integrations.ip4.status.problems')} className="space-y-1">
        <h4 className="text-xs font-medium">{t('settings.integrations.ip4.status.problems')}</h4>
        {listed === 0 ? (
          <p className="text-xs text-muted-foreground">
            {t('settings.integrations.ip4.status.noProblems')}
          </p>
        ) : (
          <Table>
            <THead>
              <TR>
                <TH scope="col">{t('settings.integrations.exportStatus.document')}</TH>
                <TH scope="col">{t('settings.integrations.status')}</TH>
                <TH scope="col">{t('settings.integrations.exportStatus.reason')}</TH>
                <TH scope="col" className="text-right">
                  {t('settings.integrations.actions')}
                </TH>
              </TR>
            </THead>
            <TBody>
              {feed.problems.map((problem) => {
                const state = requestState(problem.id);
                return (
                  <TR key={problem.id}>
                    <TD>
                      <div>
                        {t(IP4_QUEUE_LABEL_KEY[problem.queue])}
                        {problem.number !== null && (
                          <>
                            {' · '}
                            {t('settings.integrations.ip4.status.number')} {problem.number}
                          </>
                        )}
                      </div>
                      <div className="break-all font-mono text-xs text-muted-foreground">
                        {problem.documentId} · {when(problem.documentAt)}
                      </div>
                    </TD>
                    <TD>
                      <Badge variant="destructive">
                        {t(PROBLEM_STATUS_LABEL_KEY[problem.status])}
                      </Badge>
                      <div className="text-xs text-muted-foreground">
                        {t('settings.integrations.exportStatus.attempts')}: {problem.attempts}
                      </div>
                      {problem.lastAttemptAt !== null && (
                        <div className="text-xs text-muted-foreground">
                          {t('settings.integrations.ip4.status.lastAttempt')}:{' '}
                          {when(problem.lastAttemptAt)}
                        </div>
                      )}
                    </TD>
                    <TD className="max-w-md break-words text-xs">{problem.reason ?? '—'}</TD>
                    <TD className="text-right">
                      {canEdit && (
                        <Button
                          type="button"
                          variant="outline"
                          disabled={retry.isPending}
                          aria-label={`${t('settings.integrations.exportStatus.retry')} ${problem.documentId}`}
                          onClick={() => void retryRow(problem.id)}
                        >
                          {t('settings.integrations.exportStatus.retry')}
                        </Button>
                      )}
                      {state !== null && (
                        <div role="status" className="mt-1 text-xs text-muted-foreground">
                          {state}
                        </div>
                      )}
                    </TD>
                  </TR>
                );
              })}
            </TBody>
          </Table>
        )}
        {counted > listed && (
          <p className="text-xs text-muted-foreground">
            {t('settings.integrations.ip4.status.problemsTruncated')}
          </p>
        )}
      </section>

      {error !== null && (
        <p role="alert" className="text-xs text-destructive">
          {error}
        </p>
      )}
    </div>
  );
}
